import { Box, Button } from "@mui/material";
import CustomBreadcrumb from "../../../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../../../components/title/Title";
import { useEffect, useState } from "react";
import TextInput from "../../../../components/textInput/TextInput";
import { useForm } from "react-hook-form";
import { ISuccessState } from "../../../../interfaces/successState";
import { useDispatch } from "react-redux";
import { light } from "@mui/material/styles/createPalette";
import { EInputMode } from "../../../myFaults/reportFault/billComplaint/BillComplaint";
import MainLoader from "../../../../components/loading/MainLoader";
import { changePortalPassword } from "../../../../store/actions/externalUser.action";
import AlertModal from "../../../../components/alertModal/alertModal";
import { logout } from "../../../../store/actions/auth.action";

export interface IChangeContactFormInputs {
  oldPassword: string;
  newPassword: string;
  reEnterPassword: string;
}

const ChangePortalPassword = () => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
    reset,
  } = useForm<IChangeContactFormInputs>();
  const logoutHandler = () => {
    dispatch(logout());
  };

  const [isSuccess, setIsSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const newPassword = watch("newPassword");

  const reEnterPswd = watch("reEnterPassword");

  useEffect(() => {
    if (newPassword) {
      trigger("newPassword");
    }
  }, [newPassword, trigger]);

  useEffect(() => {
    if (reEnterPswd) {
      trigger("reEnterPassword");
    }
  }, [reEnterPswd, trigger]);

  const dispatch: any = useDispatch();

  const onSubmit = async (data: IChangeContactFormInputs) => {
    setLoading(true);
    setError(null);
    setIsSuccess(null);

    try {
      const response = await dispatch(changePortalPassword(data, setIsSuccess));
      setShowAlert(true);
      reset();
    } catch (err: any) {
      setError(err.message || "An error occurred while changing the password.");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const validateNewPassword = (value: string) => {
    if (!value) {
      return "Please enter a new password";
    }
    const pattern =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+-]).{8,40}$/;
    if (!pattern.test(value)) {
      return "Your password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character.";
    }
    return true;
  };

  return (
    <div className="page-container">
      <AlertModal
        show={showAlert}
        variant={error ? "error" : "success"}
        buttons={
          error
            ? [
                {
                  text: "Ok",
                  onClick: () => {
                    setShowAlert(false);
                    setError("");
                    setIsSuccess("");
                  },
                  variant: "success",
                },
              ]
            : [
                {
                  text: "Ok",
                  onClick: () => {
                    setShowAlert(false);
                    logoutHandler();
                    setError("");
                    setIsSuccess("");
                  },
                  variant: "success",
                },
              ]
        }
      >
        <label>{error || isSuccess}</label>
      </AlertModal>
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/settings/changePortalPassword",
            title: "Change Portal Password",
          },
        ]}
      />
      <Title>Change Portal Password</Title>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        mt={5}
      >
        <Box width="100%" maxWidth={400} display="flex" flexDirection="column">
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextInput
              label="Current Password"
              type="password"
              required
              mode={EInputMode.light}
              color="gray"
              useFormRegister={register("oldPassword", {
                required: "Current Password is required",
              })}
              errors={[
                {
                  isShow: !!errors.oldPassword,
                  message: errors.oldPassword?.message || "",
                },
              ]}
            />

            <TextInput
              label="New Password"
              type="password"
              required
              mode={EInputMode.light}
              color="gray"
              value={watch("newPassword")}
              useFormRegister={register("newPassword", {
                required: "New Password is required",
                validate: validateNewPassword,
              })}
              errors={[
                {
                  isShow: !!errors.newPassword,
                  message: errors.newPassword?.message || "",
                },
              ]}
            />

            <TextInput
              label="Re-Enter New Password"
              type="password"
              required
              mode={EInputMode.light}
              color="gray"
              useFormRegister={register("reEnterPassword", {
                required: "Re-enter new password is required",
                validate: (value) =>
                  value === watch("newPassword") || "Passwords do not match",
              })}
              errors={[
                {
                  isShow: !!errors.reEnterPassword,
                  message: errors.reEnterPassword?.message || "",
                },
              ]}
            />

            <Box display="flex" justifyContent="center" gap={2} mt={3}>
              <Button
                type="submit"
                variant="outlined"
                sx={{
                  borderColor: "#4CAF50",
                  color: "#4CAF50",
                  fontWeight: "bold",
                  textTransform: "none",
                  px: 4,
                  borderRadius: "30px",
                  height: "45px",
                  minWidth: "120px",
                  "&:hover": {
                    backgroundColor: "#4CAF50",
                    color: "#fff",
                    borderColor: "#4CAF50",
                  },
                }}
                disabled={loading}
              >
                Submit
              </Button>

              <Button
                type="button"
                variant="outlined"
                sx={{
                  color: "#4CAF50",
                  borderColor: "#4CAF50",
                  fontWeight: "bold",
                  textTransform: "none",
                  px: 4,
                  borderRadius: "30px",
                  height: "45px",
                  minWidth: "120px",
                  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    backgroundColor: "#4CAF50",
                    color: "#fff",
                    borderColor: "#4CAF50",
                  },
                }}
                disabled={loading}
                onClick={() => reset()}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </div>
  );
};

export default ChangePortalPassword;
